import useTranslation from '../useTranslation';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { DetailsViewEntityMenuItem } from 'components/DetailsViewEntityMenu/types';
import type { BoundaryPartData, Geometry } from 'components/Map/types';

import useApplications from 'hooks/useApplications';
import type { ApplicationResponse } from 'hooks/useApplications/types';
import useBoundary from 'hooks/useBoundary';
import useChildCreation from 'hooks/useChildCreation';
import useFarm from 'hooks/useFarm';
import type { ParcelWithClientResponse } from 'hooks/useFarm/types';
import useFields from 'hooks/useFields';
import type { SiteResponse } from 'hooks/useFields/types';
import usePageRoutes from 'hooks/usePageRoutes';
import type { PaginatedGenerator } from 'hooks/usePagination/types';

const useData = () => {
    const { farmId } = useParams();
    const { EDIT_FARM } = useTranslation();
    const { fetchFarm, isLoading } = useFarm();
    const { farmsPage, fieldDetailsPage } = usePageRoutes();
    const {
        applications,
        fetch: fetchApplications,
        isLoading: isApplicationsLoading
    } = useApplications();
    const { fetchBoundary } = useBoundary();
    const navigate = useNavigate();
    const [currentFarm, setCurrentFarm] = useState<ParcelWithClientResponse | undefined>(undefined);
    const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | undefined>(
        undefined
    );
    const [selectedBoundaryGeometry, setSelectedBoundaryGeometry] = useState<Geometry | undefined>(
        undefined
    );
    const isFirstLoad = useRef(true);
    const {
        fetch: fetchFields,
        fields,
        isLoading: isFieldsLoading,
        totalCount: totalFieldsCount
    } = useFields();
    const fieldsGenerator = useRef<PaginatedGenerator<SiteResponse> | null>(null);

    const childCreation = useChildCreation({ parentData: currentFarm, parentType: 'farm' });

    const fieldMenuItems: DetailsViewEntityMenuItem[] = (fields ?? []).map(
        ({ id = '', name = '' }) => ({
            entityId: id,
            entityName: name,
            entityRoute: fieldDetailsPage(`${id}`)
        })
    );

    const getCurrentFarm = async () => {
        if (farmId) {
            const farm = await fetchFarm({ id: farmId });

            fieldsGenerator.current = fetchFields({ farmId });
            await fieldsGenerator.current.next();

            setCurrentFarm(farm ?? undefined);
            fetchApplications({ parcelId: farmId }).next();
        }
    };

    useEffect(() => {
        if (farmId) {
            getCurrentFarm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [farmId]);

    const handleSelectApplication = async (application: ApplicationResponse) => {
        setSelectedApplicationId(application.id);

        if (!application.boundaryId) {
            setSelectedBoundaryGeometry(undefined);

            return;
        }

        const boundary = await fetchBoundary({ id: application.boundaryId });

        setSelectedBoundaryGeometry(
            boundary
                ? { isEditable: false, parts: boundary.boundaryParts as BoundaryPartData[] }
                : undefined
        );
    };

    useEffect(() => {
        if (applications && applications.length > 0 && isFirstLoad.current) {
            isFirstLoad.current = false;
            handleSelectApplication(applications[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleToggleApplications = () => {
        setIsApplicationsOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditing) {
            setIsEditing(false);
            setIsClosing(false);
            getCurrentFarm();
        }
    };

    const dropdownMenuItems = [
        {
            onClick: handleEditClick,
            text: EDIT_FARM
        },
        ...(currentFarm ? [childCreation.menuItem] : [])
    ];

    const handleNavigateBack = () => {
        navigate(farmsPage);
    };

    const handleClose = () => {
        if (isEditing) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    const handleFieldNext = async () => {
        if (!fieldsGenerator.current) {
            return { done: true };
        }

        return fieldsGenerator.current.next();
    };

    return {
        applications,
        currentFarm,
        dropdownMenuItems,
        fieldMenuItems,
        handleClose,
        handleEditClick,
        handleEditClose,
        handleFieldNext,
        handleNavigateBack,
        handleSelectApplication,
        handleToggleApplications,
        handleToggleLinkedEntities,
        isApplicationsLoading,
        isApplicationsOpen,
        isClosing,
        isEditing,
        isFieldsLoading,
        isLinkedEntitiesOpen,
        isLoading,
        selectedApplicationId,
        selectedBoundaryGeometry,
        setIsClosing,
        totalFieldsCount
    };
};

export default useData;
